/**
 * 부대 이름. 출정 화면과 전투 화면이 같은 이름을 불러야 한다.
 *
 * 부하 부대는 번호로 부른다(제2부대). 한때 맡은 동료의 이름("온달 부대")으로 불렀는데, 동료를 부관으로 옮기면
 * "온달 부대"가 온달 없이 남아 헷갈렸다. 플레이한 사람: "부하 부대의 장수를 고정하지 말아줘 … 그냥 제2부대
 * 이런식으로 표시해주면 되겠다." 맡은 동료는 부대 안(지휘 줄)에 보인다.
 */
import { commandBonus, companionCaptain } from '../sim/troops.ts';
import type { CompanionDef, Favor } from '../sim/troops.ts';

export const ALL_SQUADS_NAME = '전 부대';

export function squadName(sq: number): string {
  return sq === 0 ? '내 부대' : `제${sq + 1}부대`;
}

/** 병종의 이름(가지 코드 순서: 민병·창·도검·궁수·기병). */
export const BRANCH_NAMES: readonly string[] = ['민병', '창', '도검', '궁수', '기병'];

/** 잘 부리는 병종의 몫을 말로: "기병 공격 +8%", "창 방어 +1", "창 사기 +24". */
export function favorText(f: Favor): string {
  const who = BRANCH_NAMES[f.branch] ?? '';
  if (f.atkPct > 0) return `${who} 공격 +${f.atkPct}%`;
  if (f.defBonus > 0) return `${who} 방어 +${f.defBonus}`;
  return `${who} 사기 +${f.gritBonus}`;
}

/**
 * 화면이 보여주는 보정은 **전장에 실제로 붙는 값**이어야 한다. 그래서 정의가
 * 아니라 레벨을 얹은 값을 읽는다. 여기서 정의를 그대로 찍으면, 레벨이 오른
 * 동료를 고르고도 예전 숫자를 보게 된다.
 *
 * 출정 화면과 능력치 화면이 같이 쓴다. 따로 적으면 한쪽만 고치는 날 두 화면이 다른 값을 말한다.
 */
export function captainText(c: CompanionDef, level: number): string {
  const b = companionCaptain(c, level);
  const parts: string[] = [];
  if (b.atkPct !== 100) parts.push(`휘하 공격 ${b.atkPct - 100}% 증가`);
  if (b.defBonus > 0) parts.push(`휘하 방어 +${b.defBonus}`);
  if (b.gritBonus > 0) parts.push(`휘하 사기 +${b.gritBonus}`);
  if (b.favor) parts.push(favorText(b.favor));
  return parts.join(' · ');
}

/** 동료의 보정을 칩에 넣을 짧은 말로: "공격 +18% · 방어 +2". 없으면 빈 문자열. */
export function captainShort(c: CompanionDef, level: number): string {
  const b = companionCaptain(c, level);
  const parts: string[] = [];
  if (b.atkPct !== 100) parts.push(`공격 +${b.atkPct - 100}%`);
  if (b.defBonus > 0) parts.push(`방어 +${b.defBonus}`);
  if (b.gritBonus > 0) parts.push(`사기 +${b.gritBonus}`);
  if (b.favor) parts.push(favorText(b.favor));
  return parts.join(' · ');
}

/** 장수의 지휘를 칩에 넣을 짧은 말로: "전군 공격 +2% · 사기 +9". 아직 없으면 빈 문자열. */
export function commandShort(level: number): string {
  const b = commandBonus(level);
  const parts: string[] = [];
  if (b.atkPct !== 100) parts.push(`공격 +${b.atkPct - 100}%`);
  if (b.defBonus > 0) parts.push(`방어 +${b.defBonus}`);
  if (b.gritBonus > 0) parts.push(`사기 +${b.gritBonus}`);
  return parts.length > 0 ? `전군 ${parts.join(' · ')}` : '';
}

/** 장수의 지휘가 모든 부대에 주는 것. 전장의 `commandBonus`를 그대로 읽는다. */
export function commandText(level: number): string {
  const b = commandBonus(level);
  const parts: string[] = [];
  if (b.atkPct !== 100) parts.push(`공격 ${b.atkPct - 100}%`);
  if (b.defBonus > 0) parts.push(`방어 +${b.defBonus}`);
  if (b.gritBonus > 0) parts.push(`사기 +${b.gritBonus}`);
  return parts.length > 0 ? `지휘: 모든 부대 ${parts.join(' · ')}` : '지휘: 레벨이 오르면 모든 부대가 세진다';
}
